import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store/store';
import { clearAuth, updateBalance } from '../store/slices/authSlice';
import { ReactComponent as SolanaIcon } from '../icons/solana.svg';
import { ReactComponent as EyeIcon } from '../icons/eye.svg';
import SolanaLogo from '../components/common/SolanaLogo';
import HeadsImage from '../assets/heads.png';
import TailsImage from '../assets/tails.png';
import ProfileImage from '../assets/profile.png';
import Dropdown from '../components/common/Dropdown';
import CoinflipPopup from '../components/modals/CoinflipPopup';
import CoinflipSkeleton from '../components/common/CoinflipSkeleton';
import SEO from '../components/common/SEO';
import api from '../services/api';
import { showError, showWarning } from '../utils/toast';
import { socketService } from '../services/SocketService';
import { getProfileImageSrc } from '../utils/profileUtils';
import './CoinflipPage.css';

interface CoinflipGame {
  id: string;
  player1: {
    id: string;
    username: string;
    choice: 'heads' | 'tails';
    avatar?: string;
  };
  player2?: {
    id: string;
    username: string;
    choice: 'heads' | 'tails';
    avatar?: string;
  };
  amount: number;
  status: 'waiting' | 'playing' | 'finished';
  createdAt: string;
}

const CoinflipPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [betAmount, setBetAmount] = useState<string>('0.05');
  const [selectedChoice, setSelectedChoice] = useState<'heads' | 'tails'>('heads');
  const [sortBy, setSortBy] = useState<string>('high-to-low');
  const [amountFilter, setAmountFilter] = useState<string>('all');
  const [games, setGames] = useState<CoinflipGame[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState<CoinflipGame | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const gamesPerPage = 3;

  // Fetch waiting games
  const fetchWaitingGames = async () => {
    const startTime = Date.now();
    
    try {
      const waitingGames = await api.games.getWaitingGames();
      setGames(waitingGames);
      
      // Ensure skeleton shows for at least 3 seconds
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, 3000 - elapsed);
      
      setTimeout(() => {
        setIsPageLoading(false);
      }, remainingTime);
    } catch (error) {
      console.error('Error fetching games:', error);
      
      // Still enforce minimum time even on error
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, 3000 - elapsed);
      
      setTimeout(() => {
        setIsPageLoading(false);
      }, remainingTime);
    }
  };

  useEffect(() => {
    fetchWaitingGames();
    
    // Subscribe to real-time coinflip updates
    const unsubscribeCoinflip = socketService.subscribeToCoinflipUpdates((data) => {
      console.log('Coinflip socket update received:', data);
      
      if (data.type === 'new-game') {
        // Extract the game data (it's already spread with type added)
        const { type, ...gameData } = data;
        
        console.log('New game data:', gameData);
        
        // Add new game but prevent duplicates
        setGames(prev => {
          // Check if game already exists
          const exists = prev.some(game => game.id === gameData.id);
          if (exists) {
            console.log('Game already exists, skipping duplicate:', gameData.id);
            return prev;
          }
          console.log('Adding new game to list:', gameData.id);
          return [gameData, ...prev];
        });
      } else if (data.type === 'game-completed') {
        console.log('Game completed, removing from list:', data.gameInfo?.id);
        // Remove completed game from waiting list
        setGames(prev => prev.filter(game => game.id !== data.gameInfo?.id));
      }
    });

    // Join coinflip room for updates
    socketService.joinCoinflipRoom();

    // Reduced polling frequency for Railway free tier performance
    const interval = setInterval(fetchWaitingGames, 20000); // 20 seconds instead of 10
    
    return () => {
      unsubscribeCoinflip();
      socketService.leaveCoinflipRoom();
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // dispatch is stable, empty array is safe here

  const handleCreateGame = async () => {
    if (!user) {
      showError('Please login to create a game');
      return;
    }

    if (parseFloat(betAmount) < 0.001) {
      showWarning('Minimum bet is 0.001 SOL');
      return;
    }

    // Check if user has sufficient balance (allow exact match for max bet)
    const betAmountLamports = Math.floor(parseFloat(betAmount) * 1000000000);
    if (user.balance < betAmountLamports) {
      showError(`Insufficient balance. You need ${parseFloat(betAmount).toFixed(3)} SOL but only have ${(user.balance / 1000000000).toFixed(3)} SOL`);
      return;
    }

    setIsLoading(true);

    try {
      const newGame = await api.games.createCoinflip({
        amount: betAmountLamports, // Convert to lamports
        side: selectedChoice, // Backend expects 'side' not 'choice'
        clientSeed: Math.random().toString(36).substring(2, 15)
      });

      // Update balance if returned from API
      if (newGame.newBalance !== undefined) {
        dispatch(updateBalance(newGame.newBalance));
      }

      // Don't add the game here - let the socket event handle it to avoid race conditions
      // This ensures all clients see the game at the same time via socket
      console.log('Game created successfully, waiting for socket event:', newGame.id);
      
      // Open popup for the newly created game
      setSelectedGame(newGame);
      setIsPopupOpen(true);

    } catch (error: any) {
      console.error('Error creating game:', error);
      
      // Provide specific error messages based on the error
      if (error.message?.includes('No authentication token') || error.message?.includes('Session expired')) {
        showError('Session expired. Please login again.');
        dispatch(clearAuth());
        navigate('/login');
      } else if (error.message?.includes('401')) {
        showError('Authentication failed. Please login again.');
        dispatch(clearAuth());
        navigate('/login');
      } else if (error.message?.includes('Insufficient balance')) {
        showError('Insufficient balance to create this game.');
      } else {
        showError('Failed to create game. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChoiceSelect = (choice: 'heads' | 'tails') => {
    setSelectedChoice(choice);
  };

  const setMaxBet = () => {
    if (!user) return;
    const maxBalanceSOL = user.balance / 1000000000; // Convert lamports to SOL
    setBetAmount(maxBalanceSOL.toFixed(9)); // Use max precision to avoid rounding errors
  };

  const setHalfBet = () => {
    const current = parseFloat(betAmount) || 0;
    setBetAmount((current / 2).toFixed(3));
  };

  const handleJoinGame = (game: CoinflipGame) => {
    setSelectedGame(game);
    setIsPopupOpen(true);
  };

  const handleSpectateGame = (game: CoinflipGame) => {
    setSelectedGame(game);
    setIsPopupOpen(true);
  };

  const filterAndSortGames = (games: CoinflipGame[]) => {
    let filtered = [...games];

    // Apply amount filter
    if (amountFilter !== 'all') {
      if (amountFilter === '5+') {
        filtered = filtered.filter(game => (game.amount / 1000000000) >= 5);
      } else {
        const [minStr, maxStr] = amountFilter.split('-');
        const min = parseFloat(minStr);
        const max = parseFloat(maxStr);
        const minAmount = min * 1000000000;
        const maxAmount = max * 1000000000;
        filtered = filtered.filter(game => 
          game.amount >= minAmount && game.amount <= maxAmount
        );
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === 'high-to-low') {
        return b.amount - a.amount;
      } else {
        return a.amount - b.amount;
      }
    });

    return filtered;
  };

  const filteredGames = filterAndSortGames(games);
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredGames.length / gamesPerPage);
  const startIndex = (currentPage - 1) * gamesPerPage;
  const endIndex = startIndex + gamesPerPage;
  const paginatedGames = filteredGames.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy, amountFilter]);

  // Show skeleton while loading
  if (isPageLoading) {
    return (
      <>
        <SEO 
          title="Coinflip - SolBlox | Flip. Spin. Win."
          description="Play provably fair coinflip games on Solana. Double or nothing on the flip of a coin. Secure and transparent gaming."
          url="https://solblox.com/"
        />
        <CoinflipSkeleton />
      </>
    );
  }

  return (
    <>
      <SEO 
        title="Coinflip - SolBlox | Flip. Spin. Win."
        description="Play provably fair coinflip games on Solana. Double or nothing on the flip of a coin. Secure and transparent gaming."
        url="https://solblox.com/"
      />
      <div className="coinflip-page">
      {/* Header Section */}
      <div className="coinflip-header">
        <div className="coinflip-header-left">
          <div className="game-info">
            <h1>COINFLIP</h1>
            <p>Double or nothing on the flip of a coin</p>
          </div>
        </div>
        
        <div className="coinflip-header-right">
          <div className="bet-label">Bet Amount</div>
          <div className="bet-controls">
            <div className="bet-input-wrapper">
              <SolanaIcon className="currency-icon" />
              <input
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(e.target.value)}
                placeholder="0"
                className="bet-input-main"
                step="0.001"
                min="0.001"
                disabled={isLoading}
              />
            </div>
            <button 
              className="quick-add-btn"
              onClick={setMaxBet}
              disabled={isLoading}
            >
              Max
            </button>
            <button 
              className="quick-add-btn"
              onClick={setHalfBet}
              disabled={isLoading}
            >
              Half
            </button>
            <div className="coin-choices">
              <button
                className={`coin-choice ${selectedChoice === 'heads' ? 'selected' : 'greyed'}`}
                onClick={() => handleChoiceSelect('heads')}
                disabled={isLoading}
              >
                <img src={HeadsImage} alt="Heads" className="coin-image" />
              </button>
              <button
                className={`coin-choice ${selectedChoice === 'tails' ? 'selected' : 'greyed'}`}
                onClick={() => handleChoiceSelect('tails')}
                disabled={isLoading}
              >
                <img src={TailsImage} alt="Tails" className="coin-image" />
              </button>
            </div>
            <button 
              className="place-bet-btn" 
              onClick={handleCreateGame}
              disabled={isLoading || !user}
            >
              {isLoading ? 'Creating...' : 'Create Game'}
            </button>
          </div>
        </div>
      </div>

      {/* Games Section */}
      <div className="games-section">
        {/* Info Header */}
        <div className="games-info-header">
          <div className="left-section">
            <div className="games-count">
              <span className="count-text">All games</span>
              <span className="count-number">{filteredGames.length}</span>
            </div>
          </div>
          
          <div className="right-section">
            <div className="filters">
              {/* Sort By Filter */}
              <Dropdown
                label="Sort by"
                options={[
                  { value: 'high-to-low', label: 'High to Low' },
                  { value: 'low-to-high', label: 'Low to High' }
                ]}
                value={sortBy}
                onChange={setSortBy}
              />

              {/* Amount Filter */}
              <Dropdown
                label="Amount"
                options={[
                  { value: 'all', label: 'All' },
                  { value: '0-0.01', label: '0-0.01' },
                  { value: '0.01-0.1', label: '0.01-0.1' },
                  { value: '0.1-1', label: '0.1-1' },
                  { value: '1-5', label: '1-5' },
                  { value: '5+', label: '5+' }
                ]}
                value={amountFilter}
                onChange={setAmountFilter}
              />
            </div>
          </div>
        </div>

        {/* Games List */}
        <div className="games-list">
          {filteredGames.length === 0 ? (
            <div className="no-games">
              <p>No games available. Create the first game!</p>
            </div>
          ) : (
            paginatedGames.map((game) => {
              const avatarSrc = getProfileImageSrc(game.player1.avatar, game.player1.username);
              
              return (
                <div key={game.id} className="game-item">
                  {/* Left Player */}
                  <div className="player-section left-player">
                    <div className="player-avatar-container">
                      <img 
                        src={avatarSrc}
                        alt="Player" 
                        className="player-avatar"
                        onError={(e) => {
                          e.currentTarget.src = ProfileImage;
                        }}
                      />
                    <div className="choice-indicator">
                      <img 
                        src={game.player1.choice === 'heads' ? HeadsImage : TailsImage} 
                        alt={game.player1.choice} 
                        className="choice-coin"
                      />
                    </div>
                  </div>
                  <div className="player-name">{game.player1.username}</div>
                </div>

                {/* VS Divider */}
                <div className="vs-divider">VS</div>

                {/* Right Player (Waiting) */}
                <div className="player-section right-player">
                  <div className="player-avatar-container waiting">
                    <div className="empty-avatar">?</div>
                    <div className="choice-indicator">
                      <img 
                        src={game.player1.choice === 'heads' ? TailsImage : HeadsImage} 
                        alt={game.player1.choice === 'heads' ? 'tails' : 'heads'} 
                        className="choice-coin"
                      />
                    </div>
                  </div>
                  <div className="player-name waiting">Waiting...</div>
                </div>

                {/* Game Actions */}
                <div className="game-actions">
                  <div className="amount-display">
                    <SolanaLogo className="amount-icon" width={16} height={16} />
                    <span className="amount">{(game.amount / 1000000000).toFixed(3)}</span>
                  </div>
                  {user && user.id !== game.player1.id ? (
                    <button 
                      className="join-btn"
                      onClick={() => handleJoinGame(game)}
                    >
                      Join
                    </button>
                  ) : (
                    <button 
                      className="spectate-btn"
                      onClick={() => handleSpectateGame(game)}
                    >
                      <EyeIcon />
                      Spectate
                    </button>
                  )}
                </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {filteredGames.length > gamesPerPage && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Coinflip Popup */}
      <CoinflipPopup
        isOpen={isPopupOpen}
        onClose={() => {
          setIsPopupOpen(false);
          setSelectedGame(null);
          fetchWaitingGames(); // Refresh games when popup closes
        }}
        gameInfo={selectedGame}
        currentUserId={user?.id}
      />
    </div>
    </>
  );
};

export default CoinflipPage;